/**
 * structDoc · annotated artifact document (S04 · 9 JUN 2026).
 *
 * Replaces the abstract «★ FIELD NN» field stepper + the jagged «staircase»
 * worked-example with ONE element: the real artifact (Test Case / Use Case / Bug
 * Report / Test Plan) as it looks in a tracker, where every field carries an
 * expandable «зачем это поле» note (Genie 9 JUN: «сделать визард как выглядит
 * реальный тест-кейс — с тултипами к каждому полю · унифицировать лестницу»).
 *
 * Per-field tap toggles its note; a master button opens/closes all notes at once
 * (ADHD-friendly · not everyone taps each row).
 *
 * Markup contract (authored content · no-JS degradable — without this script every
 * `.tcf-why` shows inline and the master button is hidden via CSS):
 *   <div class="tcdoc">
 *     <div class="tcdoc-top">… id / title / chips …</div>
 *     <div class="tcdoc-bar"><span class="lab">…</span>
 *       <button type="button" class="tcdoc-allbtn" data-show="показать все подсказки"
 *         data-hide="скрыть подсказки">показать все подсказки</button></div>
 *     <div class="tcf">
 *       <div class="tcf-label"><span class="nm">ID</span><span class="q">?</span></div>
 *       <div class="tcf-cell"><div class="tcf-val">…</div><div class="tcf-why">…зачем…</div></div>
 *     </div>
 *     … fields …
 *   </div>
 */

export function initStructDoc(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  const docs = Array.from(root.querySelectorAll<HTMLElement>('.tcdoc'));
  for (const docEl of docs) {
    if (docEl.dataset.structInit === '1') continue;
    docEl.dataset.structInit = '1';
    docEl.classList.add('tcd-ready');

    const fields = Array.from(docEl.querySelectorAll<HTMLElement>('.tcf'));
    for (const field of fields) {
      const label = field.querySelector<HTMLElement>('.tcf-label');
      if (!label) continue;
      const onToggle = () => field.classList.toggle('open');
      label.addEventListener('click', onToggle);
      cleanups.push(() => label.removeEventListener('click', onToggle));
    }

    const allBtn = docEl.querySelector<HTMLButtonElement>('.tcdoc-allbtn');
    if (allBtn) {
      const showTxt = allBtn.dataset.show ?? 'показать все подсказки';
      const hideTxt = allBtn.dataset.hide ?? 'скрыть подсказки';
      const onAll = () => {
        const anyClosed = fields.some((f) => !f.classList.contains('open'));
        fields.forEach((f) => f.classList.toggle('open', anyClosed));
        allBtn.classList.toggle('on', anyClosed);
        allBtn.textContent = anyClosed ? hideTxt : showTxt;
      };
      allBtn.addEventListener('click', onAll);
      cleanups.push(() => allBtn.removeEventListener('click', onAll));
    }

    // StrictMode (dev) double-invokes effects → reset guard + ready-class on
    // cleanup so the second init rewires (see scDeck.ts for the same pattern).
    cleanups.push(() => {
      docEl.classList.remove('tcd-ready');
      delete docEl.dataset.structInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
